import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Row,
  Col,
  Tag,
  Space,
} from "antd";
import { MeetingType } from "../../config";

const { TextArea } = Input;

const MeetingTypeModal = ({
  visible,
  onCancel,
  onSubmit,
  initialValues = {},
  loading,
  error,
}) => {
  const [form] = Form.useForm();
  const [isTouched, setIsTouched] = useState(false);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue(initialValues);
      setIsTouched(false);
    }
  }, [visible, initialValues, form]);

  const handleFinish = (values) => {
    onSubmit({ ...values, meetingTypeId: initialValues?.meetingTypeId }, () =>
      form.resetFields(),
    );
    setIsTouched(false);
  };

  const handleValuesChange = (_, allValues) => {
    const changed = Object.keys(allValues).some((key) => {
      const currentValue = allValues[key] ?? "";
      const initialValue = initialValues?.[key] ?? "";

      if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
        return (
          currentValue.length !== initialValue.length ||
          currentValue.some((v, i) => v !== initialValue[i])
        );
      }

      return currentValue !== initialValue;
    });

    setIsTouched(changed);
  };

  return (
    <Modal
      title={null}
      open={visible}
      className="delete-popup"
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={null}
      centered
      width={600}
    >
      <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50">
        {initialValues?.meetingTypeId
          ? "Edit Meeting Type"
          : "Add Meeting Type"}
      </div>
      <Form
        layout="vertical"
        form={form}
        onFinish={handleFinish}
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
      >
        <div className="tw-p-6">
          {error && <p className="tw-text-red-500 tw-text-center tw-pb-2 tw-text-xs tw-font-semibold">{error}</p>}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                    Meeting Name
                  </span>
                }
                name="meetingName"
                rules={[
                  { required: true, message: "Please enter meeting name" },
                ]}
              >
                <Input placeholder="e.g. Demo Call" className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                    Duration (minutes)
                  </span>
                }
                name="durationMinutes"
                rules={[{ required: true, message: "Enter meeting duration" }]}
              >
                <InputNumber
                  min={5}
                  max={180}
                  placeholder="30"
                  className="!tw-rounded-lg !tw-border-slate-200 tw-w-full"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={
              <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                Description
              </span>
            }
            name="description"
            rules={[
              { required: true, message: "Please enter meeting description" },
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Describe what this meeting is about"
              className="!tw-rounded-lg !tw-border-slate-200"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                    Location Type
                  </span>
                }
                name="meetingPlatform"
                rules={[{ required: true, message: "Select location type" }]}
              >
                <Select
                  placeholder="Select location"
                  className="!tw-rounded-lg"
                  options={[
                    { label: "Google Meet", value: MeetingType.GOOGLE_MEET },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                    Max per day
                  </span>
                }
                name="maxPerDay"
                rules={[
                  { required: true, message: "Please enter max per day" },
                ]}
              >
                <InputNumber
                  min={1}
                  max={10}
                  placeholder="e.g. 5"
                  className="!tw-rounded-lg !tw-border-slate-200 tw-w-full"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={
              <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                Available Days
              </span>
            }
            name="availableDays"
            rules={[{ required: true, message: "Please add available Days" }]}
            className="tw-mb-2"
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Select days"
              className="!tw-rounded-lg"
              options={[
                { label: "Mon", value: "MONDAY" },
                { label: "Tue", value: "TUESDAY" },
                { label: "Wed", value: "WEDNESDAY" },
                { label: "Thu", value: "THURSDAY" },
                { label: "Fri", value: "FRIDAY" },
                { label: "Sat", value: "SATURDAY" },
                { label: "Sun", value: "SUNDAY" },
              ]}
              tagRender={(props) => (
                <Tag className="!tw-bg-indigo-50/50 !tw-border-indigo-100/30 !tw-text-theme !tw-font-bold !tw-rounded-md">
                  {props.label}
                </Tag>
              )}
            />
          </Form.Item>
        </div>
        <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
          <Button
            className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
            onClick={() => {
              onCancel();
              form.resetFields();
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="theme-btn !tw-h-[38px] !tw-rounded-lg tw-font-bold tw-text-xs tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.25)] hover:tw-scale-[1.01] tw-transition-all tw-duration-200"
            htmlType="submit"
            loading={loading}
            disabled={loading || !isTouched}
          >
            {initialValues?.meetingTypeId ? "Update" : "Create"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default MeetingTypeModal;
